import type { ViewerAnnotationState } from '@/lib/annotationAdapter';

const STORAGE_PREFIX = 'assignment-review:';
export const ASSIGNMENT_REVIEW_SAVED_EVENT = 'assignment-review-saved';
/** Bump to invalidate stale/corrupt client caches. */
const SESSION_VERSION = 3;

export interface StoredAnnotation extends ViewerAnnotationState {
  _id: string;
}

export interface AssignmentReviewSessionData {
  version?: number;
  visitedPatchIds: string[];
  /** Patch ids whose cached annotations were written after a successful load. */
  validPatchIds: string[];
  patches: Record<string, StoredAnnotation[]>;
}

function emptySession(): AssignmentReviewSessionData {
  return { version: SESSION_VERSION, visitedPatchIds: [], validPatchIds: [], patches: {} };
}

function storageKey(assignmentId: string): string {
  return `${STORAGE_PREFIX}${assignmentId}`;
}

export function getSession(assignmentId: string): AssignmentReviewSessionData {
  try {
    const raw = sessionStorage.getItem(storageKey(assignmentId));
    if (!raw) return emptySession();
    const parsed = JSON.parse(raw) as AssignmentReviewSessionData;
    if (parsed.version !== SESSION_VERSION) {
      const fresh = emptySession();
      writeSession(assignmentId, fresh);
      return fresh;
    }
    return {
      version: SESSION_VERSION,
      visitedPatchIds: Array.isArray(parsed.visitedPatchIds) ? parsed.visitedPatchIds : [],
      validPatchIds: Array.isArray(parsed.validPatchIds) ? parsed.validPatchIds : [],
      patches: parsed.patches && typeof parsed.patches === 'object' ? parsed.patches : {},
    };
  } catch {
    return emptySession();
  }
}

function writeSession(assignmentId: string, data: AssignmentReviewSessionData): void {
  sessionStorage.setItem(
    storageKey(assignmentId),
    JSON.stringify({ ...data, version: SESSION_VERSION }),
  );
}

export function clearSession(assignmentId: string): void {
  sessionStorage.removeItem(storageKey(assignmentId));
}

export function markPatchVisited(assignmentId: string, patchId: string): void {
  const session = getSession(assignmentId);
  if (!session.visitedPatchIds.includes(patchId)) {
    session.visitedPatchIds.push(patchId);
    writeSession(assignmentId, session);
  }
}

/** Persist annotations only after the matching patch finished loading. */
export function setPatchAnnotations(
  assignmentId: string,
  patchId: string,
  annotations: StoredAnnotation[],
): void {
  const session = getSession(assignmentId);
  session.patches[patchId] = annotations;
  if (!session.validPatchIds.includes(patchId)) {
    session.validPatchIds.push(patchId);
  }
  writeSession(assignmentId, session);
}

/** Returns cached anns only if they were stored after a trusted load/edit. */
export function getPatchAnnotations(
  assignmentId: string,
  patchId: string,
): StoredAnnotation[] | null {
  const session = getSession(assignmentId);
  if (!session.validPatchIds.includes(patchId)) return null;
  return session.patches[patchId] ?? null;
}

export function invalidatePatchAnnotations(assignmentId: string, patchId: string): void {
  const session = getSession(assignmentId);
  delete session.patches[patchId];
  session.validPatchIds = session.validPatchIds.filter((id) => id !== patchId);
  writeSession(assignmentId, session);
}

export function notifyReviewSaved(assignmentId: string): void {
  window.dispatchEvent(
    new CustomEvent(ASSIGNMENT_REVIEW_SAVED_EVENT, { detail: { assignmentId } }),
  );
}

export function isPatchVisited(assignmentId: string, patchId: string): boolean {
  return getSession(assignmentId).visitedPatchIds.includes(patchId);
}

export function allPatchesVisited(assignmentId: string, patchIds: string[]): boolean {
  if (patchIds.length === 0) return false;
  const visited = new Set(getSession(assignmentId).visitedPatchIds);
  return patchIds.every((id) => visited.has(id));
}
